/**
 * Utility functions for handling role-based routing logic
 */

/**
 * Determines if a user has no roles assigned
 * @param {Object} user - User object containing role information
 * @returns {boolean} - True if user has no roles
 */
export const hasNoRoles = (user) => {
  if (!user) return true;
  
  // Check both user_role and roles properties for backward compatibility
  const userRoleEmpty = user.user_role && user.user_role.length <= 0;
  const rolesEmpty = user.roles && user.roles.length <= 0;
  const noRolesProperty = !user.user_role && !user.roles;
  
  return userRoleEmpty || rolesEmpty || noRolesProperty;
};

/**
 * Gets the primary role from user data
 * @param {Object} user - User object containing role information
 * @returns {string} - Primary role or 'student' as default
 */
export const getPrimaryRole = (user) => {
  if (!user) return 'student';

  return user.roles;
};

/**
 * Determines the appropriate onboarding route based on user role and profile completion status
 * @param {Object} user - User object containing role and profile information
 * @returns {string|null} - Route path to redirect to, or null if no redirect needed
 */
export const getOnboardingRoute = (user) => {
  if (!user) return null;
  
  const primaryRole = getPrimaryRole(user);

  // Handle role-specific onboarding
  switch (primaryRole.toLowerCase()) {
    case 'student':
      // If user is a student but has no student_id, redirect to student profile onboarding
      if (!user.student_id) {
        return '/self-onboarding/student/profile';
      }
      break;
      
    case 'client':
      // If user is a client but has no client_id, redirect to client profile onboarding
      if (!user.client_id) {
        return '/self-onboarding/client/profile';
      }
      break;

    case 'institute':
      // If user is an institute but has no institute_id, redirect to institute profile onboarding
      if (user.institute_id) {
        return '/admin';
      }
      console.log('Institute profile missing');
      break;
    case 'mentor':
      // If user is a mentor but has no mentor_id, redirect to mentor profile onboarding
      if (user.mentor_id) {
        return '/admin';
      }
      console.log('Mentor profile missing');
      break;
      
    default:
      // For other roles, check if they have a client_id
      if (!user.client_id) {
        return '/self-onboarding/client/profile';
      }
      break;
  }
  
  return null; // No redirect needed
};

/**
 * Handles role-based routing logic and redirects if necessary
 * @param {Object} user - User object containing role and profile information
 * @param {Function} historyPush - History push function for navigation
 * @returns {boolean} - True if a redirect was performed, false otherwise
 */
export const handleRoleBasedRouting = (user, historyPush) => {
  const onboardingRoute = getOnboardingRoute(user);
  
  if (onboardingRoute) {
    console.log(`Redirecting user to onboarding: ${onboardingRoute}`);
    historyPush(onboardingRoute);
    return true;
  }
  
  return false;
};
